import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatDateTime, formatDate } from '../../utils/formatDateTime';
import { formatPhoneBR } from '../../utils/phone';
import {
  getLeads, getLeadsStats, updateLead, deleteLead,
} from '../../services/api';

/**
 * Gestão de leads orgânicos capturados pela landing institucional.
 *
 * Funil de vendas simples:
 *   new → contacted → qualified → proposal → converted | lost
 *
 * Master pode:
 *   - Ver todos os leads (filtrando por status ou busca)
 *   - Ver métricas de topo do funil (contagem por status + taxa de conversão)
 *   - Atualizar status de cada lead (clique no card abre o modal)
 *   - Anotar notas internas
 *   - Marcar como perdido (com motivo obrigatório)
 *   - Deletar lead
 */

const STATUS_META = {
  new:        { label: 'Novo',       color: '#3b82f6', bg: '#dbeafe', order: 1, description: 'Lead recém-capturado, aguarda primeiro contato.' },
  contacted:  { label: 'Contatado',  color: '#f59e0b', bg: '#fef3c7', order: 2, description: 'Primeiro contato já realizado.' },
  qualified:  { label: 'Qualificado', color: '#8b5cf6', bg: '#ede9fe', order: 3, description: 'Lead demonstrou interesse concreto e tem fit.' },
  proposal:   { label: 'Proposta',   color: '#06b6d4', bg: '#cffafe', order: 4, description: 'Proposta enviada, aguardando decisão.' },
  converted:  { label: 'Convertido', color: '#16a34a', bg: '#dcfce7', order: 5, description: 'Virou cliente real (tenant criado).' },
  lost:       { label: 'Perdido',    color: '#dc2626', bg: '#fee2e2', order: 6, description: 'Descartado / sem fit / não comprou.' },
};

const STATUS_ORDER = Object.entries(STATUS_META)
  .sort((a, b) => a[1].order - b[1].order)
  .map(([k]) => k);

// Wrappers finos sobre os helpers centrais — mantêm a assinatura antiga pra
// evitar edição em massa dos call sites dentro deste arquivo.
const fmtDate = formatDateTime;
const fmtDateShort = formatDate;

export default function MasterLeads() {
  const confirm = useConfirm();

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState('');
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState(null); // lead being edited
  const [form, setForm] = useState({ status: '', notes: '', lost_reason: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getLeads({ status: statusF || undefined, search: search || undefined }),
      getLeadsStats(),
    ])
      .then(([l, s]) => {
        setLeads(l.data.leads || []);
        setStats(s.data || null);
      })
      .catch(() => toast.error('Erro ao carregar leads'))
      .finally(() => setLoading(false));
  }, [statusF, search]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (lead) => {
    setEditing(lead);
    setForm({
      status: lead.status,
      notes: lead.notes || '',
      lost_reason: lead.lost_reason || '',
    });
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.status === 'lost' && !form.lost_reason.trim()) {
      return toast.error('Informe o motivo da perda.');
    }
    setSaving(true);
    try {
      await updateLead(editing.id, {
        status: form.status,
        notes: form.notes.trim() || null,
        lost_reason: form.status === 'lost' ? form.lost_reason.trim() : null,
      });
      toast.success('Lead atualizado!');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lead) => {
    const ok = await confirm({
      title: `Excluir lead "${lead.name}"?`,
      message: 'Esta ação remove o lead permanentemente do sistema. O histórico de auditoria é preservado.',
      confirmText: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteLead(lead.id);
      toast.success('Lead excluído.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  const statCards = stats ? [
    { label: 'Total',          value: stats.total,        icon: '🎯', color: 'blue'  },
    { label: 'Últimos 7d',     value: stats.last_7d,      icon: '📈', color: 'green' },
    { label: 'Taxa Conversão', value: `${stats.conversion_rate}%`, icon: '🏆', color: 'amber' },
  ] : [];

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Leads</h2>
          <p>Funil de vendas — leads orgânicos da landing</p>
        </div>
      </div>

      {/* Stats do funil */}
      {stats && (
        <>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            {statCards.map(c => (
              <div key={c.label} className="stat-card">
                <div className={`stat-icon ${c.color}`}>{c.icon}</div>
                <div>
                  <div className="stat-label">{c.label}</div>
                  <div className="stat-value" style={{ fontSize: 20 }}>{c.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Mini funnel: botões clicáveis por status com contagem */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button
              onClick={() => setStatusF('')}
              style={{
                padding: '8px 14px',
                border: '1.5px solid ' + (statusF === '' ? 'var(--primary)' : 'var(--gray-200)'),
                background: statusF === '' ? 'var(--primary)' : 'transparent',
                color: statusF === '' ? 'white' : 'var(--gray-700)',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}>
              Todos <span style={{ opacity: 0.7, marginLeft: 4 }}>({stats.total})</span>
            </button>
            {STATUS_ORDER.map(s => {
              const meta = STATUS_META[s];
              const count = stats.counts[s] || 0;
              const active = statusF === s;
              return (
                <button
                  key={s}
                  onClick={() => setStatusF(s)}
                  title={meta.description}
                  style={{
                    padding: '8px 14px',
                    border: '1.5px solid ' + (active ? meta.color : 'var(--gray-200)'),
                    background: active ? meta.color : 'transparent',
                    color: active ? 'white' : meta.color,
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}>
                  {meta.label} <span style={{ opacity: 0.7, marginLeft: 4 }}>({count})</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="filter-bar">
        <input
          className="search-input"
          placeholder="Buscar por nome, e-mail ou telefone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
      ) : leads.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎯</div>
          <h3>Nenhum lead encontrado</h3>
          <p>Os leads que chegarem pelo formulário da landing aparecem aqui.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {leads.map(lead => {
            const meta = STATUS_META[lead.status] || STATUS_META.new;
            return (
              <div
                key={lead.id}
                className="mobile-card"
                style={{
                  background: 'var(--white)',
                  borderRadius: 10,
                  border: '1px solid var(--gray-200)',
                  borderLeft: `4px solid ${meta.color}`,
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  cursor: 'pointer',
                }}
                onClick={() => openEdit(lead)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--gray-900)' }}>
                      {lead.name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                      {lead.email}{lead.phone ? ` · ${formatPhoneBR(lead.phone)}` : ''}
                    </div>
                  </div>
                  <span
                    style={{
                      background: meta.bg,
                      color: meta.color,
                      fontWeight: 700,
                      fontSize: 11,
                      padding: '4px 10px',
                      borderRadius: 12,
                      flexShrink: 0,
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}>
                    {meta.label}
                  </span>
                </div>

                {(lead.business_type || lead.plan_interest || lead.message) && (
                  <div style={{ fontSize: 12, color: 'var(--gray-600)', lineHeight: 1.5 }}>
                    {lead.business_type && <div><strong>Tipo:</strong> {lead.business_type}</div>}
                    {lead.plan_interest && <div><strong>Interesse:</strong> {lead.plan_interest}</div>}
                    {lead.message && <div style={{ fontStyle: 'italic', marginTop: 4 }}>"{lead.message}"</div>}
                  </div>
                )}

                {lead.notes && (
                  <div style={{
                    fontSize: 12,
                    background: '#fef3c7',
                    border: '1px solid #fde68a',
                    padding: '6px 10px',
                    borderRadius: 6,
                    color: '#92400e',
                  }}>
                    📝 {lead.notes}
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: 11,
                  color: 'var(--gray-400)',
                  marginTop: 4,
                  flexWrap: 'wrap',
                  gap: 6,
                }}>
                  <span>Recebido em {fmtDate(lead.created_at)}</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="btn btn-xs btn-secondary"
                      onClick={e => { e.stopPropagation(); openEdit(lead); }}>
                      Editar
                    </button>
                    <button
                      className="btn btn-xs btn-danger"
                      onClick={e => { e.stopPropagation(); handleDelete(lead); }}>
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de edição */}
      {editing && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeEdit()}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h3>{editing.name}</h3>
              <button className="modal-close" onClick={closeEdit}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body" style={{ maxHeight: 560, overflowY: 'auto' }}>
                <div style={{ marginBottom: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 6, fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.6 }}>
                  <div><strong>E-mail:</strong> {editing.email}</div>
                  {editing.phone && <div><strong>Telefone:</strong> {formatPhoneBR(editing.phone)}</div>}
                  {editing.business_type && <div><strong>Tipo:</strong> {editing.business_type}</div>}
                  {editing.plan_interest && <div><strong>Interesse:</strong> {editing.plan_interest}</div>}
                  <div><strong>Origem:</strong> {editing.source || 'landing'}</div>
                  <div><strong>Recebido em:</strong> {fmtDate(editing.created_at)}</div>
                  {editing.contacted_at && <div><strong>Primeiro contato:</strong> {fmtDate(editing.contacted_at)}</div>}
                  {editing.converted_at && <div><strong>Convertido em:</strong> {fmtDate(editing.converted_at)}</div>}
                  {editing.message && (
                    <div style={{ marginTop: 6, fontStyle: 'italic' }}>"{editing.message}"</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {STATUS_ORDER.map(s => (
                      <option key={s} value={s}>{STATUS_META[s].label}</option>
                    ))}
                  </select>
                  <small>{STATUS_META[form.status]?.description}</small>
                </div>

                {form.status === 'lost' && (
                  <div className="form-group">
                    <label>Motivo da perda *</label>
                    <input
                      value={form.lost_reason}
                      onChange={e => setForm(f => ({ ...f, lost_reason: e.target.value }))}
                      placeholder="Ex.: preço, concorrente, fora do ICP..."
                      maxLength={200}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Notas internas</label>
                  <textarea
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    rows={4}
                    placeholder="Histórico de contato, dores identificadas, follow-ups agendados..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEdit} disabled={saving}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
